import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth } from "@/api/Api";
import { getFeedV2, putChapterLike, postChapterViewed } from "../api/feedApi";
import {
  chapters$,
  feedCurrentIndex$,
  feedHasMore$,
  feedIsLoadingMore$,
  appendChapters,
  skipChapterIDs$,
} from "../state/feedState";
import { likedChapters$, setChapterLiked } from "../state/userState";
import { lesson$ } from "../state/videoState";
import { useTrackFeedEvents } from "./useTrackFeedEvents";
import { useEffect } from "react";

export const FEED_QUERY_KEY = "feedV2";

// Timestamp of the feed response last applied to chapters$. Module-level
// (like chapters$ itself) so a FeedPage remount re-emitting the CACHED
// react-query page doesn't clobber state that outlived the unmount — iOS keeps
// its feed store across tab switches and never re-seeds from a cached response.
let lastAppliedFeedAt = 0;

export function useFeedData(options?: { enabled?: boolean }) {
  const { trackFeedVideoLiked, trackFeedVideoUnliked } = useTrackFeedEvents();
  const queryClient = useQueryClient();
  const feedQuery = useQuery({
    queryKey: [FEED_QUERY_KEY],
    queryFn: () => callWithAuth(getFeedV2),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: options?.enabled ?? true,
  });

  // Sync backend data into Legend State when a GENUINELY NEW response arrives.
  // `dataUpdatedAt` distinguishes a real fetch (tags-onboarding / settings
  // refetch — replace) from a remount re-emitting the cached page (skip, so a
  // tab switch can't truncate loadMore-appended chapters, re-arm feedHasMore$,
  // resurrect dropBrokenChapter'd chapters, or strand feedCurrentIndex$ out of
  // range). `feedHasMore$` is only armed on the first-ever fetch — iOS only
  // ever sets hasReachedEnd, never clears it on a refetch; the genuine re-arm
  // happens in resetFeedPosition during a settings-driven reset.
  useEffect(() => {
    if (!feedQuery.data?.chapters) return;
    if (feedQuery.dataUpdatedAt === lastAppliedFeedAt) return;
    const isFirstFetch = lastAppliedFeedAt === 0;
    lastAppliedFeedAt = feedQuery.dataUpdatedAt;
    const skip = new Set(skipChapterIDs$.peek());
    const next = feedQuery.data.chapters.filter((c) => !skip.has(c.chapterID));
    chapters$.set(next);
    if (isFirstFetch) feedHasMore$.set(true);
    // Clamp the (persisted) index so the carousel never points past the end.
    const maxIdx = Math.max(0, next.length - 1);
    if (feedCurrentIndex$.peek() > maxIdx) feedCurrentIndex$.set(maxIdx);
    // Also clamp the lesson index against the (possibly reshaped) current
    // chapter — a refetch can leave currentLessonIndex past the new chapter's
    // lessonIDs, so chapter.lessonIDs[idx] is undefined → "video unavailable".
    const currentChapter = next[feedCurrentIndex$.peek()];
    const lessonCount = currentChapter?.lessonIDs.length ?? 0;
    const maxLessonIdx = Math.max(0, lessonCount - 1);
    if (lesson$.currentLessonIndex.peek() > maxLessonIdx) {
      lesson$.currentLessonIndex.set(maxLessonIdx);
    }
    // Seed liked state from the server only the FIRST time we see a chapter.
    // After that, local (optimistic) state wins — so a background refetch
    // (e.g. the tags-onboarding refetch) can't clobber a like the user just
    // toggled before the server registered it.
    feedQuery.data.chapters.forEach((ch) => {
      if (likedChapters$[ch.chapterID].peek() === undefined) {
        setChapterLiked(ch.chapterID, ch.isLiked);
      }
    });
  }, [feedQuery.data, feedQuery.dataUpdatedAt]);

  const toggleLike = async (chapterId: number, currentlyLiked: boolean) => {
    // Optimistic update
    setChapterLiked(chapterId, !currentlyLiked);
    // Session like/unlike counters (iOS trackFeedVideoLiked / Unliked).
    if (currentlyLiked) {
      trackFeedVideoUnliked(chapterId);
    } else {
      trackFeedVideoLiked(chapterId);
    }
    // Mark the profile Liked grid stale immediately (iOS
    // feedStore$.likedChaptersDirty → LikedTabContent invalidation).
    queryClient.invalidateQueries({ queryKey: ["likedChapters"] });
    try {
      await callWithAuth(putChapterLike, { chapterId, isLiked: !currentlyLiked });
    } catch {
      // Revert on failure
      setChapterLiked(chapterId, currentlyLiked);
    }
  };

  const markViewed = async (chapterId: number) => {
    // Ignore the end-of-feed sentinel / any non-positive id (iOS guards against
    // END_OF_FEED_CHAPTER_ID === -1 before posting, FeedNew.tsx:496-498).
    if (chapterId <= 0) return;
    try {
      await callWithAuth(postChapterViewed, { chapterId });
    } catch {
      // Fire-and-forget, non-critical
    }
  };

  /**
   * Infinite scroll: refetch the V2 feed and append deduped chapters (mirrors
   * iOS `loadMoreFeedItems`). The V2 feed serves-all then loops, so dedup is
   * what actually reaches an end — if a fetch yields zero genuinely-new
   * chapters, mark the feed exhausted so the end-of-content screen shows.
   */
  const loadMore = async () => {
    if (!feedHasMore$.peek() || feedIsLoadingMore$.peek()) return;
    feedIsLoadingMore$.set(true);
    try {
      const res = await callWithAuth(getFeedV2);
      const incoming = res?.chapters ?? [];
      const added = appendChapters(incoming);
      // Seed liked state for the freshly-added chapters.
      incoming.forEach((ch) => {
        if (likedChapters$[ch.chapterID].peek() === undefined) {
          setChapterLiked(ch.chapterID, ch.isLiked);
        }
      });
      if (added === 0) feedHasMore$.set(false);
    } catch {
      // A failed load-more shouldn't permanently end the feed; allow retry.
    } finally {
      feedIsLoadingMore$.set(false);
    }
  };

  return {
    feedQuery,
    toggleLike,
    markViewed,
    loadMore,
  };
}
